import { z } from "zod";
import { personFirstName } from "./person-name";

// TES-1355 (TES-1350 §7, Emelie 2026-10-14) — coach message templates the
// admin maintains in coach-admin, starting with the welcome message.
//
// Stored as versioned content: an `articles` row of type `coach_template`
// (draft buffer, publish-draft, ArticleRevision history, rollback; TES-207).
// The type is staff-only content: it never appears on a public or patient
// article read, and the generic article endpoints refuse to write it, so the
// only way to change a template is the coach-template routes, which validate
// the placeholders and are open to the admin role only (doctors and coaches
// never author content).
//
// A-01 "Redigerbara mallar i admin": "Admin ska varna för okända
// platshållare" and "Tillåtna platshållare i välkomstmallen är i MVP
// {förnamn} och {coachens_förnamn}. AI ska inte behövas för att fylla dem."

export const COACH_TEMPLATE_ARTICLE_TYPE = "coach_template" as const;

// Template names the routes accept, and the article key each is stored under.
export const COACH_TEMPLATE_NAMES = ["welcome"] as const;
export const coachTemplateNameSchema = z.enum(COACH_TEMPLATE_NAMES);
export type CoachTemplateName = (typeof COACH_TEMPLATE_NAMES)[number];

export const COACH_TEMPLATE_KEY_PREFIX = "coach_template.";
export function coachTemplateArticleKey(name: CoachTemplateName): string {
  return `${COACH_TEMPLATE_KEY_PREFIX}${name}`;
}
export const COACH_WELCOME_TEMPLATE_KEY = coachTemplateArticleKey("welcome");

/** True for any key in the coach-template namespace, known name or not. */
export function isCoachTemplateKey(key: string): boolean {
  return key.startsWith(COACH_TEMPLATE_KEY_PREFIX);
}

export const COACH_TEMPLATE_PLACEHOLDERS = [
  "förnamn",
  "coachens_förnamn",
] as const;
export type CoachTemplatePlaceholder =
  (typeof COACH_TEMPLATE_PLACEHOLDERS)[number];

/**
 * TES-1189 / TES-1344 (Ö-408) — Emelie's welcome template, verbatim from A-01
 * "Beslutad svensk grundmall" (Coach Admin Surface, doc modified 2026-10-13),
 * with the paragraphing and the emojis as they stand in the Google Doc.
 *
 * This is the seed of the `coach_template.welcome` article and the fallback
 * the welcome draft uses when no template is published.
 */
/**
 * TES-1402 (Ö-581, Emelie 2026-10-15) — the one sentence about medical
 * questions an AI coach draft may contain: "AI-utkast får förklara att
 * medicinska frågor tas vidare men får aldrig ge medicinska råd." It is the
 * welcome template's own line, so the draft and the template say the same
 * thing. The draft guard lets this sentence through verbatim and nothing
 * else medical (coach-draft.service).
 */
export const COACH_MEDICAL_FORWARDING_SENTENCE =
  "Har du frågor om din medicinska behandling hjälper jag dig att ta dom vidare till vårdteamet.";

export const COACH_WELCOME_TEMPLATE_SV = [
  "Hej {förnamn}!",
  "Välkommen till Ella 🤗 Jag heter {coachens_förnamn} och kommer att vara din coach på den här resan. Jag finns här för dig under behandlingen och stöttar med det som händer i vardagen när det kommer till vanor, motivation och det som känns svårt eller oklart på vägen.",
  "Varje vecka får du en kort incheckning i appen om hur du mår. Jag läser det du delar med mig och hör av mig när det finns något att prata om. Du kan också alltid skriva till mig här i chatten däremellan.",
  COACH_MEDICAL_FORWARDING_SENTENCE,
  "Du behöver inte svara på det här meddelandet. Jag ville bara säga hej och att jag finns här när du behöver 😊",
].join("\n\n");

/**
 * TES-1402 (Ö-589–590, Emelie 2026-10-15) — "Välkomstmallen måste bevara
 * coachidentitet, icke-medicinsk avgränsning och vägen för medicinska
 * frågor." Checked on publish; a draft may lack them while it is worked on.
 *
 *  - coach_identity: the coach's name, `{coachens_förnamn}`.
 *  - non_medical_boundary: a sentence about medical questions that says the
 *    coach does not answer them herself: she takes them further ("vidare"),
 *    or gives no medical advice ("inga medicinska råd").
 *  - medical_question_path: a sentence about medical questions that says
 *    where they go (the care team or the doctor).
 *
 * A phrase check, not an understanding of the text: it accepts the A-01
 * template and rewordings that keep these words, and refuses a template from
 * which the sentence was removed. One sentence may carry both medical parts,
 * as the A-01 template's does.
 */
export const COACH_WELCOME_REQUIRED_PARTS = [
  "coach_identity",
  "non_medical_boundary",
  "medical_question_path",
] as const;
export type CoachWelcomeRequiredPart =
  (typeof COACH_WELCOME_REQUIRED_PARTS)[number];

const MEDICAL_RE = /medicinsk/iu;
const NOT_ANSWERED_HERE_RE =
  /(?<![\p{L}])vidare(?![\p{L}])|(?<![\p{L}])(?:inte|inga|ingen)(?![\p{L}])[^.!?\n]*råd/iu;
const MEDICAL_DESTINATION_RE = /vårdteam|läkare/iu;

export function missingWelcomeTemplateParts(
  text: string,
): CoachWelcomeRequiredPart[] {
  const sentences = text
    .split(/(?<=[.!?])\s+|\n+/u)
    .filter((t) => MEDICAL_RE.test(t));
  const missing: CoachWelcomeRequiredPart[] = [];
  if (!text.includes("{coachens_förnamn}")) missing.push("coach_identity");
  if (!sentences.some((t) => NOT_ANSWERED_HERE_RE.test(t))) {
    missing.push("non_medical_boundary");
  }
  if (!sentences.some((t) => MEDICAL_DESTINATION_RE.test(t))) {
    missing.push("medical_question_path");
  }
  return missing;
}

// The admin-facing title of the seeded row.
export const COACH_WELCOME_TEMPLATE_TITLE = "Välkomstmeddelande";

// Anything in braces counts as a placeholder, so a typo such as
// "{fornamn}" or "{ förnamn }" is caught rather than sent to a patient.
const PLACEHOLDER_RE = /\{([^{}]*)\}/g;

/**
 * Every placeholder in `text` that is not allowed, in order of first
 * appearance, braces included ("{efternamn}"). A stray single brace is
 * reported too, since it is either a broken placeholder or a typo.
 */
export function unknownCoachTemplatePlaceholders(text: string): string[] {
  const allowed = new Set<string>(COACH_TEMPLATE_PLACEHOLDERS);
  const unknown: string[] = [];
  const re = new RegExp(PLACEHOLDER_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (!allowed.has(m[1]) && !unknown.includes(m[0])) unknown.push(m[0]);
  }
  const rest = text.replace(PLACEHOLDER_RE, "");
  for (const brace of ["{", "}"]) {
    if (rest.includes(brace) && !unknown.includes(brace)) unknown.push(brace);
  }
  return unknown;
}

export const COACH_TEMPLATE_MAX_CHARS = 4000;

// What the admin saves as a template draft. Swedish only: the coach writes to
// the patient in Swedish, and the template has no English counterpart.
export const coachTemplateDraftInputSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1)
    .max(COACH_TEMPLATE_MAX_CHARS)
    .superRefine((body, ctx) => {
      const unknown = unknownCoachTemplatePlaceholders(body);
      if (unknown.length > 0) {
        ctx.addIssue({
          code: "custom",
          message: `unknown_placeholders:${unknown.join(",")}`,
        });
      }
    }),
});
export type CoachTemplateDraftInput = z.infer<
  typeof coachTemplateDraftInputSchema
>;

export const coachTemplateRollbackSchema = z.object({
  toVersion: z.number().int().positive(),
});

// One published version in the template's history.
export const coachTemplateRevisionSchema = z.object({
  version: z.number().int().positive(),
  body: z.string(),
  changedBy: z.string().nullable(),
  changedAt: z.string(),
});
export type CoachTemplateRevision = z.infer<typeof coachTemplateRevisionSchema>;

// The admin editor view of one template.
export const coachTemplateViewSchema = z.object({
  name: coachTemplateNameSchema,
  title: z.string(),
  // The live text, and whether it is published at all.
  body: z.string(),
  status: z.enum(["draft", "published"]),
  version: z.number().int().positive(),
  updatedAt: z.string(),
  // The pending edit, not yet live.
  draft: z
    .object({
      body: z.string(),
      updatedAt: z.string(),
    })
    .nullable(),
  revisions: z.array(coachTemplateRevisionSchema),
  placeholders: z.array(z.string()),
});
export type CoachTemplateView = z.infer<typeof coachTemplateViewSchema>;

// What a coach's draft reads: the published text, or null when nothing is
// published (the client then falls back to the code template).
export const publishedCoachTemplateSchema = z.object({
  body: z.string().nullable(),
  version: z.number().int().positive().nullable(),
});
export type PublishedCoachTemplate = z.infer<
  typeof publishedCoachTemplateSchema
>;

/**
 * Fill a welcome template. Both placeholders go through `personFirstName`
 * (TES-1277), so a registry "MAJA" reads "Maja" and no name is ever shown in
 * hardcoded capitals.
 *
 *  - "{förnamn}": the patient's first name. With no name on file (an
 *    anonymised patient, or none registered) the placeholder and the space
 *    before it go, so "Hej {förnamn}!" reads "Hej!"; A-01 does not cover that
 *    case.
 *  - "{coachens_förnamn}": the first name of the coach writing it.
 *
 * A template that fails `unknownCoachTemplatePlaceholders` is never published,
 * so nothing else in braces reaches this.
 */
export function renderCoachTemplate(
  template: string,
  input: { coachName: string; patientName?: string | null },
): string {
  const patientFirstName = input.patientName
    ? personFirstName(input.patientName)
    : "";
  const coachFirstName = personFirstName(input.coachName);
  const withPatient = patientFirstName
    ? template.split("{förnamn}").join(patientFirstName)
    : template.replace(/ ?\{förnamn\}/g, "");
  return withPatient.split("{coachens_förnamn}").join(coachFirstName);
}

// Sample names for the admin preview. A-01: "Förhandsvisningen ska använda
// testdata, inte en verklig patients uppgifter."
export const COACH_TEMPLATE_PREVIEW_NAMES = {
  patientName: "Maja",
  coachName: "Anna",
} as const;
